import { z } from 'zod';
import * as R from 'remeda';

import {
  DateTimeSchema,
  objectIdRef,
  ObjectIdSchema,
  ObjectIdString,
  subDocument,
} from '../common.js';
import { AccountDTO } from './accountSchema.js';
import {
  ActivityDataMappingConfig,
  ChromeExtensionConfig,
  DataIQConfig,
  EntityFields,
  TalentIQConfig,
} from './configSchemas.js';
import { StaffIQConfig } from './staffIqConfigSchema.js';
import { RelatedEntityMetadataMap } from '../../interfaces.js';

// ------------------------------------------------------------------------------------------------------------------ //
//region IntegrationConfig schemas

export const SsnRedactionMode = z.enum(['redact', 'keepLast4', 'preserve']);
export type SsnRedactionMode = z.infer<typeof SsnRedactionMode>;

export const RedactionConfig = z.object({
  ssnRedaction: SsnRedactionMode.optional(),
});
export type RedactionConfig = z.infer<typeof RedactionConfig>;

export const NlpConfig = z.object({
  spanishFalsePositiveFiltering: z.boolean().default(false),
});
export type NlpConfig = z.infer<typeof NlpConfig>;

export const AnalyticsConfig = z.object({
  enabled: z.boolean().nullish(),
});
export type AnalyticsConfig = z.infer<typeof AnalyticsConfig>;

export const IntegrationConfig = z.object({
  preloadNotes: z.boolean().default(true),
  /** @deprecated */
  preloadInsights: z.boolean().default(false).meta({ deprecated: true }),
  preloadJobs: z.boolean().default(true),
  preloadJobProfiles: z.boolean().default(false),
  redactionConfig: RedactionConfig,
  nlpConfig: NlpConfig,
  talentIqConfig: TalentIQConfig,
  dataIqConfig: DataIQConfig,
  activityDataMappings: ActivityDataMappingConfig.array(),
  /** @deprecated */
  entityFields: EntityFields,
  staffIqConfig: StaffIQConfig,
  analyticsConfig: AnalyticsConfig.nullish(),
  chromeExtensionConfig: ChromeExtensionConfig.optional(),
});
export type IntegrationConfig = z.infer<typeof IntegrationConfig>;

export const SimpleConfig = IntegrationConfig.pick({
  preloadNotes: true,
  preloadInsights: true,
  preloadJobs: true,
  preloadJobProfiles: true,
});
export type SimpleConfig = z.infer<typeof SimpleConfig>;

// SimpleConfig with all defaults removed
export const SimpleConfigUpdate = SimpleConfig.required().partial();
export type SimpleConfigUpdate = z.infer<typeof SimpleConfigUpdate>;

//endregion
// ------------------------------------------------------------------------------------------------------------------ //
//region Base Integration types
export const AtsType = z.enum(['bullhorn', 'microsoftDynamics']);
export type AtsType = z.infer<typeof AtsType>;

export const NonAtsIntegrationType = z.enum(['textUs']);
export type NonAtsIntegrationType = z.infer<typeof NonAtsIntegrationType>;

export const IntegrationType = z.enum([...AtsType.options, ...NonAtsIntegrationType.options]);
export type IntegrationType = z.infer<typeof IntegrationType>;

export const IntegrationStatus = z.enum(['active', 'disabled', 'terminated', 'deleted']);
export type IntegrationStatus = z.infer<typeof IntegrationStatus>;

export const IntegrationSecretSchemaName = z.enum([
  'bullhornIntegrationSecret',
  'textUsIntegrationSecret',
  'microsoftDynamicsSecret',
]);
export type IntegrationSecretSchemaName = z.infer<typeof IntegrationSecretSchemaName>;

export const INTEGRATION_TO_SECRET_NAME = {
  bullhorn: 'bullhornIntegrationSecret',
  textUs: 'textUsIntegrationSecret',
  microsoftDynamics: 'microsoftDynamicsSecret',
} as const satisfies Record<IntegrationType, IntegrationSecretSchemaName>;
export type SecretNameForIntegration<T extends IntegrationType> =
  (typeof INTEGRATION_TO_SECRET_NAME)[T];

export const SecretMetadata = z.object({
  secretName: z.string(),
  version: z.string(),
  schemaName: IntegrationSecretSchemaName,
  schemaVersion: z.number().int(),
  tags: z.record(z.string(), z.string()),
  updatedAt: DateTimeSchema,
});
export interface SecretMetadata<
  T extends IntegrationSecretSchemaName = IntegrationSecretSchemaName,
> extends z.infer<typeof SecretMetadata> {
  schemaName: T;
}

export const IIntegration = z.object({
  _id: ObjectIdSchema,
  name: z.string().optional(),
  account: objectIdRef('Account'),
  status: IntegrationStatus.default('active'),
  type: IntegrationType,
  apiData: z.record(z.string(), z.unknown()),
  config: IntegrationConfig.partial().nullish(),
  secretMetadata: subDocument(SecretMetadata).nullish(),
  metadata: z.object({
    relatedEntities: RelatedEntityMetadataMap,
  }),
  createdAt: DateTimeSchema,
  updatedAt: DateTimeSchema,
});
export type IIntegration = z.infer<typeof IIntegration>;

//endregion
// ------------------------------------------------------------------------------------------------------------------ //
//region Bullhorn Integrations

export const BullhornSessionData = z.object({
  redirectUri: z.string().nullish(),
  oauthUrl: z.string().nullish(),
  userId: z.string().nullish(),
  restServices: z.string().nullish(),
  restUrl: z.string().nullish(),
  bhRestToken: z.string().nullish(),
  refreshToken: z.string().nullish(),
});
export type BullhornSessionData = z.infer<typeof BullhornSessionData>;

export const BullhornIntegrationApiData = z.object({
  enableBullhornInternalApi: z.boolean().default(false),
  authUrl: z.string().nullish().default(''),
  restUrl: z.string().nullish().default(''),
  redirectUri: z.string().nullish().default('https://toroinsights.ai'),
  swimlane: z.string().nullish().default(''),
  corpId: z.string().nullish().default(''),
  sessionData: BullhornSessionData.default({}),
  internalSessionData: BullhornSessionData.default({}),
  entityFieldExclusions: z.record(z.string(), z.string().array()).default({}),
});
export type BullhornIntegrationApiData = z.infer<typeof BullhornIntegrationApiData>;

export const BullhornIntegrationApiDataInput = BullhornIntegrationApiData.required().partial();

export const IBullhornIntegration = IIntegration.safeExtend({
  type: z.literal('bullhorn'),
  apiData: BullhornIntegrationApiData,
  config: IntegrationConfig,
  metadata: z.object({
    relatedEntities: RelatedEntityMetadataMap,
  }),
  secretMetadata: subDocument(
    SecretMetadata.safeExtend({ schemaName: z.literal('bullhornIntegrationSecret') })
  ).nullish(),
});
export type IBullhornIntegration = z.infer<typeof IBullhornIntegration>;

//endregion
// ------------------------------------------------------------------------------------------------------------------ //
//region Microsoft Dynamics Integrations

export const IMicrosoftDynamicsIntegration = IIntegration.safeExtend({
  type: z.literal('microsoftDynamics'),
  apiData: z.object({}),
  // config: z.object({}),
  secretMetadata: subDocument(
    SecretMetadata.safeExtend({ schemaName: z.literal('microsoftDynamicsSecret') })
  ).nullish(),
});
export type IMicrosoftDynamicsIntegration = z.infer<typeof IMicrosoftDynamicsIntegration>;

//endregion
// ------------------------------------------------------------------------------------------------------------------ //
//region DTOs

export const IntegrationDTO = z.object({
  _id: ObjectIdString,
  account: AccountDTO.or(ObjectIdString),
  status: IntegrationStatus,
  type: IntegrationType,
  name: z.string().optional(),
  corpId: z.string().nullish(),
  apiUserId: z.string().nullish(),
  // Exclude activityDataMappings from the general DTO because they're huge and prone to errors if misconfigured
  config: IntegrationConfig.omit({ activityDataMappings: true }).partial().nullish(),
  createdAt: DateTimeSchema.optional(),
  updatedAt: DateTimeSchema.optional(),
  metadata: z.object({ relatedEntities: RelatedEntityMetadataMap }).partial().optional(),
});
export type IntegrationDTO = z.infer<typeof IntegrationDTO>;

export const PopulatedIntegrationDTO = IntegrationDTO.safeExtend({ account: AccountDTO });
export type PopulatedIntegrationDTO = z.infer<typeof PopulatedIntegrationDTO>;

export const IntegrationCreateSchema = z.object({
  accountId: ObjectIdString,
  type: IntegrationType,
  status: IntegrationStatus.optional(),
  name: z.string().optional(),
});

//endregion
// ------------------------------------------------------------------------------------------------------------------ //
//region Integration secrets

/**
 * Helper to get a _schemaVersion property
 * @param current - Current version number. Must be a positive integer
 */
function schemaVersionNumber(current: number) {
  if (!Number.isInteger(current) || current < 1) {
    throw new Error(`Invalid schemaVersionNumber: ${current}`);
  }
  return z.number().int().positive().max(current).default(current);
}

const SecretDataBase = z.object({
  _schemaName: IntegrationSecretSchemaName.optional(),
  _schemaVersion: z.number().int().optional(),
});

export const BullhornSecretData = SecretDataBase.safeExtend({
  _schemaName: z.literal('bullhornIntegrationSecret').default('bullhornIntegrationSecret'),
  _schemaVersion: schemaVersionNumber(1),
  clientId: z.string().default(''),
  clientSecret: z.string().default(''),
  username: z.string().default(''),
  password: z.string().default(''),
  internalUsername: z.string().default(''),
  internalPassword: z.string().default(''),
});
export type BullhornSecretData = z.infer<typeof BullhornSecretData>;

export const MicrosoftDynamicsSecretData = SecretDataBase.safeExtend({
  _schemaName: z.literal('microsoftDynamicsSecret').default('microsoftDynamicsSecret'),
  _schemaVersion: schemaVersionNumber(1),
});
export type MicrosoftDynamicsSecretData = z.infer<typeof MicrosoftDynamicsSecretData>;

export const TextUsSecretData = SecretDataBase.safeExtend({
  _schemaName: z.literal('textUsIntegrationSecret').default('textUsIntegrationSecret'),
  _schemaVersion: schemaVersionNumber(1),
  apiKey: z.string().default(''),
  webhookSecret: z.string().default(''),
});
export type TextUsSecretData = z.infer<typeof TextUsSecretData>;

// Validates any integration secret by its self describing _schemaName tag
export const IntegrationSecretData = z.discriminatedUnion('_schemaName', [
  BullhornSecretData,
  TextUsSecretData,
  MicrosoftDynamicsSecretData,
]);
export type IntegrationSecretData<
  T extends IntegrationSecretSchemaName = IntegrationSecretSchemaName,
> = z.infer<typeof IntegrationSecretData> & { _schemaName: T };

export const BullhornSecretValues = BullhornSecretData.omit({
  _schemaName: true,
  _schemaVersion: true,
});
export const BullhornSecretValuesInput = BullhornSecretValues.required().partial();

// A reveal response only includes fields that actually have a value set in Secret Manager.
export const BullhornRevealedCredentials = BullhornSecretValuesInput;
export type BullhornRevealedCredentials = z.infer<typeof BullhornRevealedCredentials>;

// Identifiers, non secrets, stored in the same vault blob, getCredentialIdentifiers filters them out server-side.
export const BullhornCredentialIdentifiers = BullhornSecretValues.pick({
  clientId: true,
  username: true,
  internalUsername: true,
});
export type BullhornCredentialIdentifiers = z.infer<typeof BullhornCredentialIdentifiers>;

// tracks if each field has a value. Unfilled fields render editable and filled ones render masked.
export const BullhornCredentialFieldsPresence = z.object(
  R.mapValues(BullhornSecretValues.shape, () => z.boolean())
);
export type BullhornCredentialFieldsPresence = z.infer<typeof BullhornCredentialFieldsPresence>;

//endregion
// ------------------------------------------------------------------------------------------------------------------ //
